import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import type { Person } from '@/components/Combobox/PersonSelectorCombobox';
import { SimpleDatePicker } from '@/components/DatePicker/SimpleDatePicker';
import { Spacer, VStack } from '@/components/Layout';
import { FullScreenOverlay } from '@/components/Loading';
import { WorkerSelect } from '@/components/Select/WorkerSelect';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useAllowIndividualRequestOperationPackageQuery } from '@/routes/_individual/individual/-query/useAllowIndividualRequestOperationPackageQuery';
import { useNavigateBackOrFallback } from '../../-hooks/useNavigateBackOrFallback';
import { navigateToFirstIncompleteSelfServiceTask } from '../../-utils/navigateToFirstIncompleteSelfServiceTask';
import { type TargetPersonFilter, useTargetSelection } from './-hook';
import { useGetAllowIndividualRequestOperationPackagePattern, usePostSelfServiceStartOperationPackagePattern } from './-query';
export const Route = createFileRoute('/_individual/individual/operation-package/$id/pattern/$patternId/')({
  component: RouteComponent
});
function RouteComponent() {
  const {
    i18n
  } = useTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationPackage'
  });
  const {
    id,
    patternId
  } = Route.useParams();
  const navigate = Route.useNavigate();
  const {
    data: operationPackage
  } = useAllowIndividualRequestOperationPackageQuery(id);
  const {
    data: operationPackagePattern
  } = useGetAllowIndividualRequestOperationPackagePattern(id, patternId);
  const targetSelection = useTargetSelection(operationPackage.packageType);
  const [anchorDate, setAnchorDate] = useState<Date | undefined>(undefined);
  const [selectedTargetId, setSelectedTargetId] = useState<string | undefined>(undefined);
  const {
    mutate,
    isPending
  } = usePostSelfServiceStartOperationPackagePattern(id, patternId, {
    onSuccess: data => {
      void navigateToFirstIncompleteSelfServiceTask(navigate, data.operationPackageInstanceId);
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  });
  const handleBack = useNavigateBackOrFallback(`/individual/operation-package/${id}`);
  const handleStartOperationPackageButtonClick = () => {
    if (!anchorDate || targetSelection?.showSelection && !selectedTargetId) return;
    mutate({
      anchorDate: format(anchorDate, 'yyyy-MM-dd'),
      targetId: selectedTargetId
    });
  };
  return <VStack className="p-6 gap-3 items-start max-w-dialog-lg mx-auto w-full bg-neutral-fill min-h-full">
      <ActionButton icon="ChevronLeft" variant="ghost" size="sm" className="ml-[-8px]" onClick={handleBack}>
        {t('navigateToSelectPattern')}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{operationPackage.name}</Text>
        <Text variant="labelLarge">{operationPackagePattern.name}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <VStack className="gap-2">
        <Text variant="label" className="font-semibold text-neutral-muted-on-fill">
          {operationPackagePattern.anchorDateLabel}
        </Text>
        <SimpleDatePicker value={anchorDate} onChange={setAnchorDate} disablePast={false} showYearMonthSelect={false} />
      </VStack>
      <Spacer variant="s" />

      {targetSelection?.showSelection && <>
          {targetSelection?.target === 'Person' && <TargetPersonSelection onChangeTarget={id => setSelectedTargetId(id)} filter={targetSelection.targetPersonFilter} />}
          <Spacer variant="s" />
        </>}
      <ActionButton onClick={handleStartOperationPackageButtonClick} className="w-full" disabled={!anchorDate}>
        {t('page.startOperationPackage.startOperationPackage', {
        operationPackageName: operationPackage.name
      })}
      </ActionButton>
      {isPending && <FullScreenOverlay />}
    </VStack>;
}
function TargetPersonSelection(p: {
  onChangeTarget: (targetId: string) => void;
  filter?: TargetPersonFilter;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationPackage'
  });
  const [selectedTarget, setSelectedTarget] = useState<Person | undefined>(undefined);
  const handleChange = (option?: Person) => {
    setSelectedTarget(option);
    p.onChangeTarget(option?.id ?? '');
  };
  return <VStack className="gap-2">
      <Text variant="label" className="font-semibold text-neutral-muted-on-fill">
        {t('page.startOperationPackage.requestFor')}
      </Text>
      <WorkerSelect value={selectedTarget} onChange={handleChange} allowEmpty={false} category={p.filter?.category} excludeNotHaveWorkerEmail={p.filter?.excludeNotHaveWorkerEmail} />
    </VStack>;
}